import React from "react";

import { Box } from "@mui/material";

import { BackgroundStyle, LetterCase, SubtitleStyles } from "../../types";

interface CaptionSampleProps {
  words: string[];
  activeIndex: number;
  styles: SubtitleStyles;
  fontSize: number;
}

const applyCase = (text: string, letterCase?: LetterCase) => {
  if (letterCase === LetterCase.UPPERCASE) return text.toUpperCase();
  if (letterCase === LetterCase.LOWERCASE) return text.toLowerCase();
  if (letterCase === LetterCase.CAPITALIZE) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return text;
};

// CSS has no text outline that matches ASS borders; eight offset shadows are
// the closest cross-browser approximation.
export const outlineShadow = (thickness: number, color: string) => {
  if (!thickness) return "none";
  const t = Math.max(1, thickness);
  return [
    [t, 0],
    [-t, 0],
    [0, t],
    [0, -t],
    [t, t],
    [-t, -t],
    [t, -t],
    [-t, t],
  ]
    .map(([x, y]) => `${x}px ${y}px 0 ${color}`)
    .join(", ");
};

// Pop grows the word ~6% of its width per side; pad by about that much so it
// never covers its neighbours.
const horizontalPadding = (animation: string, word: string) => {
  if (animation === "box") return "0.18em";
  if (animation === "pop") return `${0.04 * word.length}em`;
  return 0;
};

export default function CaptionSample({
  words,
  activeIndex,
  styles,
  fontSize,
}: CaptionSampleProps) {
  const animation = styles.caption_animation ?? "none";
  const baseColor = styles.color || "#FFFFFF";
  const highlight = styles.highlight_color || "#FACC15";
  const outline = Math.min(3, (styles.outline_thickness ?? 0) * 0.6);
  const visible =
    animation === "single_word"
      ? [{ word: words[activeIndex] ?? words[0], index: activeIndex }]
      : words.map((word, index) => ({ word, index }));
  const hasBackground =
    styles.background_style && styles.background_style !== BackgroundStyle.NONE;

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: `${fontSize * 0.1}px ${fontSize * 0.28}px`,
        px: hasBackground ? 1 : 0,
        py: hasBackground ? 0.5 : 0,
        borderRadius:
          styles.background_style === BackgroundStyle.ROUNDED ? 1 : 0,
        bgcolor: hasBackground
          ? `${styles.background_color || "#000000"}CC`
          : "transparent",
        fontFamily: `"${styles.font || "Inter"}", sans-serif`,
        fontSize,
        fontWeight: styles.bold ? 800 : 500,
        fontStyle: styles.italic ? "italic" : "normal",
        letterSpacing: `${(styles.letter_spacing ?? 0) * 0.5}px`,
        lineHeight: 1.15,
        textAlign: "center",
      }}
    >
      {visible.map(({ word, index }) => {
        const isActive = index === activeIndex;
        const isSpoken = index <= activeIndex;
        let color = baseColor;
        if (animation === "karaoke" && isSpoken) color = highlight;
        if ((animation === "highlight" || animation === "pop") && isActive) {
          color = highlight;
        }
        if (animation === "box" && isActive) {
          color = styles.highlight_text_color || "#FFFFFF";
        }
        const popScale =
          (animation === "pop" || animation === "single_word") && isActive
            ? 1.12
            : 1;
        return (
          <Box
            component="span"
            key={`${index}-${word}`}
            sx={{
              display: "inline-block",
              color,
              px: horizontalPadding(animation, word),
              borderRadius: "0.04em",
              bgcolor:
                animation === "box" && isActive ? highlight : "transparent",
              textShadow: outlineShadow(
                outline,
                styles.outline_color || "#000",
              ),
              transform: `scale(${popScale})`,
              transition:
                "transform 140ms cubic-bezier(.34,1.56,.64,1), color 80ms, background-color 80ms",
              animation:
                animation === "single_word"
                  ? "captionPopIn 180ms cubic-bezier(.34,1.56,.64,1)"
                  : undefined,
              "@keyframes captionPopIn": {
                from: { transform: "scale(0.6)", opacity: 0.3 },
                to: { transform: "scale(1.12)", opacity: 1 },
              },
            }}
          >
            {applyCase(word, styles.letter_case)}
          </Box>
        );
      })}
    </Box>
  );
}
